import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import {
  CtBatch,
  CtBatchApiService,
  CtDocument,
  CtHeaderComponent,
  CtPage,
  DocumentPreviewComponent,
  getSortedDocuments,
  OverlayDimensionService,
  PageImageService,
  ProofCtBatch,
  SharedCtBatchStoreService,
} from '@ps-gui/claims-terminal-shared';
import { ActivatedRoute } from '@angular/router';
import { LoadingPageComponent } from '@ps-gui/shared-core';
import { forkJoin, last, map, of, switchMap, tap } from 'rxjs';
import {
  LassoSelectionDirective,
  OcrService,
  squeezeMap,
  TextSelectionOverlayComponent,
} from '@ps-gui/claims-terminal-extraction';
import { ExternalDocumentPreviewOcrSelectionDirective } from './external-document-preview-ocr-selection.directive';
import { AuditCopyService } from '@ps-gui/legacy';

@Component({
  selector: 'external-preview',
  templateUrl: './external-preview.component.html',
  imports: [
    DocumentPreviewComponent,
    LoadingPageComponent,
    CtHeaderComponent,
    TextSelectionOverlayComponent,
    ExternalDocumentPreviewOcrSelectionDirective,
    LassoSelectionDirective,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ExternalPreviewComponent implements OnInit {
  private readonly ctBatchApiService = inject(CtBatchApiService);
  private readonly auditCopyService = inject(AuditCopyService);
  private readonly route = inject(ActivatedRoute);

  protected readonly pageImageService = inject(PageImageService);
  protected readonly dimensionService = inject(OverlayDimensionService);
  protected readonly ctBatchStoreService = inject(SharedCtBatchStoreService);

  private readonly ocrService = inject(OcrService);

  protected readonly loadingComplete = computed(() => this.pageImageService.loadingProgress() >= 1);

  protected readonly batches = signal<CtBatch[]>([]);
  protected readonly documents = signal<CtDocument[]>([]);

  protected readonly ocrInitialized = signal(false);

  ngOnInit(): void {
    const auditId = this.route.snapshot.params['auditId'];

    const $sourceBatches = this.auditCopyService
      .getAuditSourceId(auditId)
      .pipe(
        switchMap((sourceId) =>
          sourceId ? this.ctBatchApiService.searchCtBatchesByAuditId(sourceId) : of([]),
        ),
      );

    const $auditBatches = this.ctBatchApiService.searchCtBatchesByAuditId(auditId);

    forkJoin([$sourceBatches, $auditBatches])
      .pipe(
        map(([sourceBatches = [], auditBatches = []]) => [...sourceBatches, ...auditBatches]),
        switchMap((batches) =>
          forkJoin(batches.map((batch) => this.ctBatchApiService.getCtBatch(batch.id))),
        ),
        squeezeMap((batches) => {
          const pages = batches.flatMap((batch) =>
            batch.documents.flatMap((document) => document.pages),
          );
          return this.ocrService.initForPages(pages).pipe(
            tap((finishedCount) =>
              console.debug(`Loaded ocr for ${finishedCount} of ${pages.length} pages`),
            ),
            last(),
          );
        }),
        tap(() => {
          this.ocrInitialized.set(true);
        }),
        tap((batches) => this.batches.set(batches)),
        tap((batches) => this.initCtBatch(batches)),
        map((batches) => batches.flatMap((batch) => batch.documents)),
        tap((documents) => this.documents.set(getSortedDocuments(documents))),
        tap(() => this.pageImageService.preloadThumbnails(...this.batches())),
        map((document) => document.flatMap((document: CtDocument) => document.pages)),
        map((page) => page.map((page: CtPage) => page.id)),
        last(),
      )
      .subscribe();
  }

  private initCtBatch(batches: CtBatch[]) {
    // TODO: consider different domains
    const batch = batches[0] as ProofCtBatch;

    // The preview shows documents for all ct batches of a proof, but we only need
    // one proof to render the meta-information - so we just take the first one.
    this.ctBatchStoreService.ctBatch = batch;
  }
}
